/**
 * What the Plugins dialog offers: the first-party plugins and kits, and how
 * the dev server and the browser version recognise a game's own plugins (a
 * plugin file in its source, or an installed package that says it is one).
 * project.json lists plugins by package name, or by a path from its folder
 * such as `./src/plugins/doors.ts`.
 */

/** A plugin project.json can list. */
export interface PluginInfo {
  /** What project.json lists: a package name, or a path from project.json's folder starting with `./`. */
  name: string;
  label: string;
  description?: string;
  /** The plugin's id, such as `animo.arcade`, when known before it loads. */
  id?: string;
  /** A kit sets the editor up for one kind of game; a project has one. */
  kit?: boolean;
  /** For a kit: the kind of game, as a new project offers it, such as `Escape room`. */
  game?: string;
  /** Names of the plugins it brings along. */
  includes?: string[];
}

/** The plugins and kits animo-editor ships, in the order the dialog shows them. */
export const FIRST_PARTY_PLUGINS: readonly PluginInfo[] = [
  {
    name: '@animo-editor/kit-adventure',
    label: 'Escape-room kit',
    id: 'animo.adventure',
    kit: true,
    game: 'Escape room',
    includes: ['@animo-editor/plugin-ui'],
    description: 'Rooms, hotspots, locks, items and the puzzle flow board.',
  },
  {
    name: '@animo-editor/kit-platformer',
    label: 'Platformer kit',
    id: 'animo.platformer',
    kit: true,
    game: 'Platformer',
    includes: ['@animo-editor/plugin-arcade'],
    description: 'Levels, player, enemies, moving platforms, pickups, zones and jump tuning.',
  },
  { name: '@animo-editor/plugin-arcade', label: 'Arcade physics', id: 'animo.arcade', description: 'Bodies and collision groups, set in the inspector.' },
  {
    name: '@animo-editor/plugin-spine',
    label: 'Spine skeletons',
    id: 'animo.spine',
    description: 'Spine 4.3 skeletons, skins and a Spine dock tab. Needs a Spine Editor licence.',
  },
  { name: '@animo-editor/plugin-ui', label: 'UI', id: 'animo.ui', description: 'UI scenes: buttons, bars, stacks, sliders and toggles.' },
  {
    name: '@animo-editor/plugin-touch',
    label: 'Touch controls',
    id: 'animo.touch',
    description: 'An on-screen stick and buttons for phones and tablets, pressing the input map’s actions.',
  },
  { name: '@animo-editor/plugin-couch', label: 'Couch Games', id: 'animo.couch', description: 'Upload the game to Couch Games as its test version.' },
];

/** Where a plugin on offer comes from. */
export type PluginOrigin = 'first-party' | 'file' | 'package';

/** A plugin the dialog can add, and whether this project can load it as things stand. */
export interface PluginCandidate extends PluginInfo {
  origin: PluginOrigin;
  /** False when the package isn't in the game's node_modules (dev server), or the site lacks it (browser version). */
  installed: boolean;
  version?: string;
}

/** What the project's host can offer the Plugins dialog. */
export interface PluginListing {
  candidates: PluginCandidate[];
  /** The dev server can run the package manager in the game's folder. */
  canInstall: boolean;
  /** Plugins can be added by any package name or path, not only from the list. */
  canAddByName: boolean;
}

/** The package.json keyword that marks a package as an animo-editor plugin. */
export const PLUGIN_KEYWORD = 'animo-editor-plugin';

/** True for source text that defines a plugin: imports the runtime and makes a plugin, and isn't a script. */
export function isPluginSource(code: string): boolean {
  if (!code.includes('@animo-editor/runtime') || /\bextends\s+Script\b/.test(code)) return false;
  return /\bdefinePlugin\s*[(<]|\bAnimoPlugin\b/.test(code) && /\bexport\s+default\b/.test(code);
}

/** The plugin id a plugin file sets, such as `mygame.doors`, read from its text. */
export function pluginIdIn(code: string): string | undefined {
  return /\bid\s*:\s*['"]([A-Za-z0-9_.-]+)['"]/.exec(code)?.[1];
}

/** True for a name project.json lists as a path rather than a package. */
export const isPluginPath = (name: string) => name.startsWith('./') || name.startsWith('../') || name.startsWith('/');

/** True for an npm package name, optionally with a version or tag (`@scope/name@^1.2`). */
export function isPackageName(name: string): boolean {
  return /^(@[a-z0-9][a-z0-9._~-]*\/)?[a-z0-9][a-z0-9._~-]*(@[A-Za-z0-9.^~=*_-]+)?$/.test(name) && name.length <= 214;
}

/** The package name without a version: `@scope/name@^1.2` becomes `@scope/name`. */
export function packageNameOf(spec: string): string {
  const at = spec.indexOf('@', 1);
  return at < 0 ? spec : spec.slice(0, at);
}

/** The first-party entry for a name, if it is one. */
export function firstPartyPlugin(name: string): PluginInfo | undefined {
  return FIRST_PARTY_PLUGINS.find((p) => p.name === name);
}
